<script setup>
  import {inject, onMounted, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {usePage} from "@inertiajs/vue3";
  import MergeAccountIconGroup from "@pages/MergeAccount/Components/IconGroup.vue";
  import {debounce} from "es-toolkit";

  import Crypt from "@/resources/js/crypt.js";

  defineOptions({name: "MergeAccountShowStepsFinished"});

  defineProps({
    processingPercentage: {
      type: Number,
      required: true,
    },
  });

  const emit = defineEmits(["ready"]);

  const pageProperties = usePage().props;

  const privacy = inject("$privacy");

  const sourcePlaintext = ref(null);
  const targetPlaintext = ref(null);

  const sourceKey = ref(null);
  const targetKey = ref(null);

  const sourceValidation = ref(null);
  const targetValidation = ref(null);

  onMounted(() => {
    // which key do we already know
    // from the logged-in user?
    // source...
    if (privacy.getClient()?.id === pageProperties.source.id) {
      sourceKey.value = privacy.getRecoveryCode().split(" ").join("");
    }
    // ...or target?
    if (privacy.getClient()?.id === pageProperties.target.id) {
      targetKey.value = privacy.getRecoveryCode().split(" ").join("");
    }
  });

  watch(
    () => sourcePlaintext.value,
    debounce((plaintextPassword) => {
      const temporaryCrypt = new Crypt("");

      // collect all users.userencryption in a single array
      const userEncryptions = pageProperties.source.users.map((user) => user.userencryption);

      temporaryCrypt.sha256(plaintextPassword).then((hashedPassword) => {
        const checkCrypt = new Crypt(hashedPassword);

        Promise.any(userEncryptions.map((value) => checkCrypt.enableStrictMode().decrypt(value)))
          .then((decrypted) => {
            sourceKey.value = decrypted;
          })
          .catch((error) => {
            console.error("Error decrypting source user encryption:", error);
            sourceValidation.value = "Das Datenschutzpasswort ist nicht korrekt.";
          });
      });
    }, 1000),
  );

  watch(
    () => targetPlaintext.value,
    debounce((plaintextPassword) => {
      const temporaryCrypt = new Crypt("");

      // collect all users.userencryption in a single array
      const userEncryptions = pageProperties.target.users.map((user) => user.userencryption);

      temporaryCrypt.sha256(plaintextPassword).then((hashedPassword) => {
        const checkCrypt = new Crypt(hashedPassword);

        Promise.any(userEncryptions.map((value) => checkCrypt.enableStrictMode().decrypt(value)))
          .then((decrypted) => {
            targetKey.value = decrypted;
          })
          .catch((error) => {
            console.error("Error decrypting target user encryption:", error);
            targetValidation.value = "Das Datenschutzpasswort ist nicht korrekt.";
          });
      });
    }, 1000),
  );

  watch([() => sourceKey.value, () => targetKey.value], ([newSourceKey, newTargetKey]) => {
    if (newSourceKey !== null && newTargetKey !== null) {
      emit("ready", {sourceKey: newSourceKey, targetKey: newTargetKey});
    }
  });
</script>

<template>
  <div>
    <MergeAccountIconGroup>
      <ComponentIcon v-if="$page.props.source.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
      <ComponentIcon v-else class="shrink-0" custom="front-desk" />

      <ComponentIcon class="text-4xl">published_with_changes</ComponentIcon>

      <ComponentIcon v-if="$page.props.target.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
      <ComponentIcon v-else class="shrink-0" custom="front-desk" />
    </MergeAccountIconGroup>

    <h3 class="mt-4 text-xl font-semibold">Ihre Accounts sind erfolgreich verknüpft!</h3>

    <p class="mt-4">
      Ihr
      <span v-if="$page.props.source.third_party_type === 'p4u'">pharma4u-Account</span>
      <span v-else>Warenwirtschaftsaccount</span>
      und ihr
      <span v-if="$page.props.target.third_party_type === 'p4u'">pharma4u-Account</span>
      <span v-else>Warenwirtschaftsaccount</span>
      sind nun über eine gemeinsame Patientenübersicht verknüpft. Diese ist über ein gemeinsames Datenschutzpasswort
      erreichbar.
    </p>

    <ComponentAlert v-if="$page.props?.target?.license_recommendation" type="info" class="mt-8">
      <p>
        Als Nutzer des MediCheck Connect in Ihrer Warenwirtschaft erhalten Sie die Pro-Version zu einem vergünstigten
        Preis.
      </p>
      <p class="mt-4">So aktivieren Sie Ihren Rabatt:</p>
      <ul class="list-dash ml-2">
        <li>
          Genen Sie zu
          <MedLink
            variant="primary"
            href="https://www.pharma4u.de/medicheck-connect-pro"
            target="_blank"
            :data-test="getTestId('steps-finished-connect-pro-link')"
          >
            pharma4u.de/medicheck-connect-pro
          </MedLink>
        </li>
        <li>Um die Lizenz umzustellen, buchen Sie dort die Pro-Version.</li>
        <li>
          Achten Sie darauf, die E-Mail-Adresse Ihred pharma4u-Accounts zu verwenden. Nur so wird der Rabatt angerechnet
          und die Lizenz umgestellt.
        </li>
      </ul>
    </ComponentAlert>
  </div>
</template>
